import type { MessageKey } from "./en";

/** French UI catalog. Every key in {@link en} must be present. */
export const fr: Record<MessageKey, string> = {
  "app.title": "Rescript — montez vos vidéos comme du texte",
  "common.cancel": "Annuler",
  "common.close": "Fermer",
  "common.delete": "Supprimer",
  "common.download": "Télécharger",
  "common.import": "Importer",
  "common.loading": "Chargement",
  "common.remove": "Retirer",
  "common.restore": "Restaurer",
  "common.retry": "Réessayer",
  "common.searchOrCreate": "Rechercher ou créer…",
  "common.settings": "Réglages",
  "common.system": "Système",
  "common.tools": "Outils",
  "settings.appearance": "Apparence",
  "settings.light": "Clair",
  "settings.dark": "Sombre",
  "settings.interfaceLanguage": "Langue de l’interface",
  "settings.privacy": "Confidentialité",
  "settings.helpImprove": "Aider à améliorer l’app",
  "settings.telemetryHelp": "Envoyer des statistiques anonymes d’utilisation des fonctionnalités et des rapports de plantage.",
  "settings.support": "Assistance / retours",
  "settings.reportIssue": "Signaler un problème",
  "settings.homepage": "Page d’accueil",
  "settings.github": "GitHub",
  "settings.followX": "Suivre sur X",
  "model.transcriptSource": "Source de transcription",
  "model.language": "Langue",
  "model.transcriptLanguage": "Langue de transcription",
  "model.importTranscript": "Importer une transcription",
  "upload.recentProjects": "Projets récents",
  "upload.removeRecent": "Retirer des récents",
  "upload.dropPrefix": "Déposez un fichier vidéo ou audio ici, ou",
  "upload.browse": "parcourir",
  "upload.chooseTranscriptFirst": "Choisissez une transcription dans le menu ci-dessus, puis déposez votre média",
  "upload.willUseTranscript": "Utilisera {name} · MP4, WebM, MOV, MP3, WAV, …",
  "upload.mediaFormats": "MP4, WebM, MOV, MP3, WAV, M4A, …",
  "upload.gettingReady": "Préparation",
  "upload.gettingReadyHelp": "Configuration du moteur multimédia, cela n’arrive qu’une fois.",
  "upload.unsupported": "Ce navigateur ne peut pas lancer l’éditeur",
  "upload.unsupportedHelp":
    "Le montage nécessite SharedArrayBuffer, qui demande une page isolée entre origines. Essayez une version récente de Chrome, Edge, Safari ou Firefox via HTTPS.",
  "upload.unsupportedSimd": "Cet appareil ne peut pas exécuter l’éditeur",
  "upload.unsupportedSimdHelp": "L’édition nécessite WebAssembly SIMD, que ce processeur ne prend pas en charge. Sur un PC Windows sur Arm, installez la dernière version de Rescript : les installeurs actuels contiennent une version Arm64 native qui ne s’exécute pas en émulation.",
  "upload.transcribeTitle": "Transcrire",
  "upload.transcribeText": "Whisper en local, ou importez SRT / VTT.",
  "upload.editTitle": "Monter",
  "upload.editText": "Sélectionnez des mots et appuyez sur Supprimer pour monter.",
  "upload.exportTitle": "Exporter",
  "upload.exportText": "Rendez le montage final en MP4 ou M4A.",
  "editor.chooseTranscript": "Choisissez d’abord un fichier de transcription dans le menu source.",
  "editor.chooseMedia": "Choisissez un fichier vidéo ou audio.",
  "editor.undo": "Annuler (⌘Z)",
  "editor.redo": "Rétablir (⇧⌘Z)",
  "editor.export": "Exporter",
  "topbar.startOver": "Recommencer",
  "transcript.replace": "Remplacer la transcription depuis SRT, VTT ou JSON",
  "transcript.header": "Transcription",
  "transcript.wordDeleted": "{count} mot",
  "transcript.wordsDeleted": "{count} mots",
  "transcript.replaceConfirm": "Remplacer la transcription actuelle par ce fichier ?",
  "transcript.invalidFile": "Choisissez un fichier de transcription SRT, VTT ou JSON.",
  "transcript.noSpeech": "Aucune parole détectée. Vérifiez que ce fichier contient de l’audio, ou importez une transcription.",
  "transcript.cut": "Couper",
  "transcript.follow": "Suivre la tête de lecture",
  "transcript.hideDeleted": "Masquer les mots supprimés",
  "transcript.showDeleted": "Afficher les mots supprimés",
  "transcript.correct": "Corriger",
  "transcript.scrollWithPlayhead": "Faire défiler avec la tête de lecture",
  "transcript.joinSplit": "Clip scindé — cliquez pour joindre ces clips",
  "transcript.joinClips": "Joindre les clips",
  "transcript.hesitation": "Hésitation détectée (non transcrite) — coupez avec Retirer les mots de remplissage",
  "transcript.textLabel": "Texte de la transcription",
  "transcript.textHint": "Les flèches parcourent les mots et déplacent la tête de lecture. Maj avec les flèches sélectionne. Retour arrière ou Suppr coupe les mots, Entrée les corrige, Espace lance ou met en pause, et Annuler rétablit les coupes.",
  "transcript.textHesitation": "[hésitation]",
  "transcript.textCut": "Coupé : {text}",
  "transcript.textNothingToCut": "Rien à couper",
  "transcript.textUndo": "Annuler",
  "transcript.textRedo": "Rétablir",
  "tools.bulk": "Nettoyages groupés de transcription",
  "tools.removeFillers": "Retirer les mots de remplissage",
  "tools.removeFillersTitle": "Couper les mots de remplissage (\"euh\", \"hum\", \"...\", …) de la vidéo",
  "tools.restoreFillers": "Restaurer les mots de remplissage",
  "tools.restoreFillersTitle": "Remettre chaque mot de remplissage coupé",
  "tools.removeSilences": "Retirer les silences",
  "tools.removeSilencesTitle": "Couper les pauses et silences (≥{seconds} s) de la vidéo",
  "tools.restoreSilences": "Restaurer les silences",
  "tools.restoreSilencesTitle": "Remettre chaque pause coupée",
  "timeline.removed": "{trimmed} retiré — durée d’origine {duration}",
  "timeline.back5": "Reculer de 5 s",
  "timeline.playPause": "Lecture / pause (espace)",
  "timeline.forward5": "Avancer de 5 s",
  "timeline.split": "Scinder",
  "timeline.splitTitle": "Scinder le clip à la tête de lecture (S)",
  "timeline.splitDisabled": "Placez la tête de lecture sur une zone conservée pour scinder",
  "timeline.delete": "Supprimer",
  "timeline.deleteTitle": "Supprimer le clip sélectionné (Suppr)",
  "timeline.deleteDisabled": "Sélectionnez un clip sur la timeline pour le supprimer",
  "timeline.restore": "Restaurer",
  "timeline.restoreTitle": "Restaurer la coupe sélectionnée (Suppr)",
  "timeline.restoreDisabled": "Sélectionnez une coupe ou une section de silence sur la timeline pour la restaurer",
  "timeline.zoomOut": "Dézoomer",
  "timeline.fit": "Adapter à la fenêtre",
  "timeline.zoomIn": "Zoomer — faites glisser les bords des mots pour affiner le timing",
  "timeline.joinClips": "Joindre ces clips (retirer la scission)",
  "timeline.trimStart": "Rogner le début du clip",
  "timeline.trimEnd": "Rogner la fin du clip",
  "timeline.hesitationAdjust": "… hésitation détectée — faites glisser les bords pour ajuster, ou coupez avec Retirer les mots de remplissage",
  "timeline.hesitationCut": "… hésitation détectée — coupez avec Retirer les mots de remplissage",
  "timeline.dragTiming": "{word} — faites glisser les bords pour ajuster le timing",
  "timeline.scrollZoom": "Faites défiler pour zoomer / dézoomer",
  "speaker.moveStart": "Faites glisser pour déplacer le début de ce locuteur",
  "speaker.moveLabel": "Déplacer l’étiquette du locuteur",
  "speaker.options": "Options du locuteur",
  "speaker.change": "Changer de locuteur",
  "speaker.rename": "Renommer le locuteur",
  "speaker.renameAction": "Renommer",
  "speaker.replace": "Remplacer dans le projet par…",
  "speaker.removeProject": "Retirer du projet",
  "speaker.newName": "Nouveau nom",
  "speaker.find": "Rechercher un locuteur…",
  "speaker.noMatches": "Aucun locuteur correspondant",
  "speaker.renameTo": "Renommer {current} en {next}",
  "speaker.button": "Locuteur",
  "speaker.search": "Rechercher ou créer…",
  "speaker.defaultName": "Locuteur {number}",
  "speaker.create": "Créer \"{name}\"",
  "export.title": "Exporter",
  "export.type": "Type d’export",
  "export.video": "Vidéo",
  "export.audio": "Audio",
  "export.transcript": "Transcription",
  "export.subtitles": "Sous-titres",
  "export.videoUnavailable": "L’export vidéo n’est pas disponible pour les projets audio uniquement",
  "export.noAudio": "Ce fichier n’a pas de piste audio",
  "export.noWordsFirst": "Transcrivez ou importez d’abord une transcription",
  "export.format": "Format",
  "export.resolution": "Résolution",
  "export.original": "Original",
  "export.plainText": "TXT",
  "export.markdown": "Markdown",
  "export.statOriginal": "Original",
  "export.statCuts": "Coupes",
  "export.statEdited": "Monté",
  "export.includeTimestamps": "Inclure les horodatages",
  "export.speakerLabels": "Inclure les noms des locuteurs",
  "export.shortCues": "Sous-titres courts (≤5 s)",
  "export.transcriptHelp": "Texte avec étiquettes de locuteur et coupes retirées.",
  "export.transcriptHelpTimestamps":
    "Texte avec étiquettes de locuteur et coupes retirées. Chaque tour commence par son temps sur la timeline montée.",
  "export.transcriptHelpPdf":
    "Le PDF utilise une police latine standard — préférez DOCX pour l’Unicode complet (CJK, etc.).",
  "export.subtitlesHelp":
    "SRT et VTT utilisent la timeline montée (coupes appliquées). JSON conserve la liste complète des mots pour une réimportation.",
  "export.subtitlesHelpShortCues":
    "SRT et VTT utilisent la timeline montée (coupes appliquées), découpés en sous-titres courts par phrase (≤5 s). JSON conserve la liste complète des mots pour une réimportation.",
  "export.encodingHelp": "Réencodage avec ffmpeg.wasm — les fichiers longs prennent un moment.",
  "export.reexport": "Réexporter avec les dernières modifications",
  "export.rendering": "Rendu dans votre navigateur…",
  "export.downloadFile": "Télécharger {name}",
  "export.saveFile": "Enregistrer {name}…",
  "export.saving": "Enregistrement…",
  "export.saveDialogTitle": "Enregistrer l’export",
  "export.savedAs": "Enregistré sous {name}",
  "export.showInFinder": "Afficher dans le Finder",
  "export.showInExplorer": "Afficher dans l’Explorateur",
  "export.showInFolder": "Afficher dans le dossier",
  "export.renderingNative": "Rendu en cours…",
  "export.encodingHelpNative": "Réencodage avec ffmpeg — les fichiers longs prennent un moment.",
  "export.downloadFormat": "Télécharger .{format}",
  "export.exportFormat": "Exporter {format}",
  "export.timeline": "Timeline",
  "export.timelineTitle": "Exporter une séquence NLE / DAW (Resolve, Premiere, Final Cut, Pro Tools, Reaper, Blender)",
  "export.nle": "Format",
  "export.frameRate": "Cadence",
  "export.timelineHelp":
    "La séquence référence le média original par nom de fichier — reliez-le dans le NLE après l’import.",
  "export.timelineHelpAaf": "AAF Pro Tools / Logic (métadonnées uniquement).",
  "export.timelineHelpFcpx": "FCPXML Final Cut Pro.",
  "export.timelineHelpPremiere": "XML Adobe Premiere Pro (xmeml).",
  "export.timelineHelpResolve": "XML DaVinci Resolve (xmeml).",
  "export.timelineHelpReaper": "Projet Reaper (.rpp).",
  "export.timelineHelpSamplitude":
    "EDL Samplitude (.edl) — s’ouvre aussi directement dans Reaper.",
  "export.timelineHelpBlender":
    "Projet de montage vidéo Blender (.blend, Blender 5.2+) — enregistrez-le dans le même dossier que le média d’origine.",
  "export.aafOverCap":
    "Ce montage a {count} clips ; AAF en prend en charge jusqu’à {max}. Utilisez Resolve, Premiere, Final Cut, Reaper ou EDL Samplitude.",
  "export.preparing": "Préparation…",
  "import.reading": "Lecture de la transcription…",
  "import.importing": "Importation…",
  "import.failed": "Échec de l’import",
  "import.readingFile": "Lecture du fichier…",
  "import.chooseFileShort": "Choisir un fichier…",
  "import.chooseFile": "Choisir une transcription SRT, VTT ou JSON",
  "import.selected": "{name} sélectionné",
  "banner.faster": "Vous voulez des transcriptions et exports plus rapides ?",
  "banner.getDesktop": "Obtenir l’app de bureau Rescript",
  "banner.downloadFor": "Télécharger pour {platform}",
  "banner.dismiss": "Ignorer",
  "social.githubRepo": "Dépôt GitHub",
  "social.discordServer": "Serveur Discord",
  "social.xProfile": "Profil X",
  "globalError.title": "Rescript — une erreur est survenue",
  "globalError.heading": "Une erreur est survenue",
  "globalError.body":
    "L’éditeur a rencontré une erreur inattendue. Vos projets enregistrés sont toujours sur cet appareil ; un rechargement devrait les restaurer.",
  "progress.loadingMedia": "Chargement du média…",
  "progress.loadingMediaEngine": "Chargement du moteur multimédia…",
  "progress.extractingAudio": "Extraction de l’audio…",
  "progress.preparingMedia": "Préparation du média…",
  "progress.loadingSpeechModel": "Chargement du modèle vocal…",
  "progress.loadingSpeechCache": "Chargement du modèle vocal depuis le cache…",
  "progress.downloadingSpeech": "Téléchargement du modèle vocal…",
  "progress.gpuFallback": "GPU interrompu — poursuite sur CPU…",
  "progress.detectingSpeech": "Détection de la parole…",
  "progress.transcribing": "Transcription…",
  "progress.loadingAlignCache": "Chargement du modèle d’alignement depuis le cache…",
  "progress.downloadingAlign": "Téléchargement du modèle d’alignement…",
  "progress.aligning": "Alignement des mots…",
  "progress.speakers": "Identification des locuteurs…",
  "error.selectModel": "Sélectionnez un modèle vocal pour transcrire.",
  "error.workerCrashed": "Le worker de transcription a planté.",
  "error.mediaEngineNetwork": "Impossible de charger le moteur multimédia — la connexion a été interrompue. Vérifiez votre connexion internet et réessayez.",
  "error.mediaEngineStalled": "Le moteur multimédia ne répondait plus et a été redémarré. Fermez d’autres applications et onglets pour libérer de la mémoire, puis réessayez.",
  "error.mediaEngineNotReady": "Le moteur multimédia n’est pas encore prêt — rechargez la page et réessayez.",
  "error.simdUnsupported": "Ce processeur ne prend pas en charge WebAssembly SIMD, dont le moteur multimédia a besoin. Sur un PC Windows sur Arm, installez la dernière version de Rescript : les installeurs actuels contiennent une version Arm64 native qui ne s’exécute pas en émulation.",
  "error.processFile": "Impossible de traiter ce fichier.",
  "error.extractAudio": "Impossible d’extraire l’audio de ce fichier.",
  "error.nothingToExport": "Tout a été supprimé — rien à exporter.",
  "error.videoExport": "L’export a échoué pendant le rendu de la vidéo.",
  "error.audioExport": "L’export a échoué pendant le rendu de l’audio.",
  "error.exportSave": "Impossible d’enregistrer le fichier exporté.",
  "error.diskFull": "Espace disque insuffisant pour terminer. Libérez de l’espace et réessayez.",
  "error.export": "Échec de l’export.",
  "error.timelineExport": "Échec de l’export de la timeline.",
  "error.timelineEmpty": "Rien à placer sur la timeline.",
  "error.aafTemplate": "Impossible de charger le modèle d’export AAF.",
  "error.emptyTranscript": "Ce fichier de transcription est vide.",
  "error.noTimedWords": "Aucun mot horodaté trouvé dans cette transcription.",
  "error.parseJson": "Impossible d’analyser cette transcription JSON.",
  "error.jsonShape": 'JSON doit être un tableau de mots ou { "words": [...] }.',
  "error.noWords": "Aucun mot à exporter.",
  "error.projectMissing": "Ce projet n’est plus enregistré.",
  "error.openProject": "Impossible d’ouvrir ce projet.",
  "error.removeProject": "Impossible de retirer ce projet.",
  "error.readTranscript": "Impossible de lire cette transcription.",
  "error.clearRecent": "Impossible d’effacer les projets récents.",
  "error.modelDownload":
    "Impossible de terminer le téléchargement du modèle vocal — la connexion a été interrompue. Vérifiez votre connexion internet et réessayez ; les parties déjà téléchargées sont conservées.",
  "error.gpuReset": "La transcription a été interrompue lors de la réinitialisation du GPU (souvent après le verrouillage de l’écran). Veuillez réessayer.",
  "confirm.clearRecent": "Retirer tous les projets récents ? Leurs modifications enregistrées seront supprimées.",
};
